// ==================================
// File: src/pages/dashboard/DashboardPage.jsx
// FULL + RESPONSIVE + PAGINATED + ESLINT CLEAN
// ==================================

import { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Stack,
  Avatar,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Button,
  TablePagination,
  Chip,
} from "@mui/material";


import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TimerIcon from "@mui/icons-material/Timer";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

import { Line } from "react-chartjs-2";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

// ===== Chart.js v4 FIX =====
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);

  // 🔔 Weekly announcement
  const [weeklyAnnouncement, setWeeklyAnnouncement] = useState(null);

  // 🔢 Pagination states
  const [weakPage, setWeakPage] = useState(0);
  const [weakRows, setWeakRows] = useState(5);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRows, setHistoryRows] = useState(5);

  // ================= DATA LOAD =================
  useEffect(() => {
    api.get("/dashboard/student").then((res) => setData(res.data));
    api.get("/challenges/weekly-announcement").then((res) => {
      setWeeklyAnnouncement(res.data);
    });
  }, []);

  if (!data) {
    return (
      <Typography variant="h6" sx={{ mt: 4 }}>
        Loading dashboard...
      </Typography>
    );
  }

  const {
    summary,
    weak_subjects,
    trends,
    all_results,
    last_exam = null,
    best_subject = null,
  } = data;

  // ================= TRIAL LOGIC =================
  const isTrialUser =
    user?.is_trial_active === true &&
    user?.has_active_subscription !== true;

  const remainingTrials =
    isTrialUser && user?.trial_exam_limit != null
      ? user.trial_exam_limit - user.trial_exams_used
      : 0;

  const goToUpgrade = () => {
    navigate("/dashboard/my-subscriptions");
  };

  // ================= CHART DATA =================
  const chartData = {
    labels: trends.map((t) =>
      new Date(t.submitted_at).toLocaleDateString("en-US")
    ),
    datasets: [
      {
        label: "Score (%)",
        data: trends.map((t) => t.score),
        borderColor: "#1976d2",
        backgroundColor: "rgba(25,118,210,0.15)",
        borderWidth: 3,
        tension: 0.35,
      },
    ],
  };

  // ================= STAT CARD =================
  const StatCard = ({ icon, label, value }) => (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar sx={{ bgcolor: "#e3f2fd", color: "#1976d2" }}>
          {icon}
        </Avatar>
        <Stack>
          <Typography variant="overline" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );

  // ================= PAGINATION =================
  const paginatedWeak = weak_subjects.slice(
    weakPage * weakRows,
    weakPage * weakRows + weakRows
  );

  const paginatedHistory = all_results.slice(
    historyPage * historyRows,
    historyPage * historyRows + historyRows
  );

  return (
    <Box sx={{ px: { xs: 1, md: 2 } }}>

	
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        Student Dashboard
      </Typography>

      {/* ================= WEEKLY ANNOUNCEMENT ================= */}
      {weeklyAnnouncement && (
        <Alert
          severity="info"
          sx={{ mb: 3, borderLeft: "6px solid #1976d2" }}
        >
          <Stack spacing={1}>
            <Typography fontWeight="bold">
              📣 Weekly Challenge Announcement
            </Typography>

            <Typography>{weeklyAnnouncement.message}</Typography>

            {weeklyAnnouncement.subjects?.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {weeklyAnnouncement.subjects.map((s) => (
                  <Chip
                    key={s.id}
                    label={`${s.name} (${s.code})`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Alert>
      )}

      {/* ================= TRIAL BANNER ================= */}
      {isTrialUser && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <WarningAmberIcon />
              <Typography>
                You have <strong>{remainingTrials}</strong> trial exam(s)
                remaining.
              </Typography>
            </Stack>
            <Button
              variant="contained"
              color="warning"
              startIcon={<ArrowUpwardIcon />}
              onClick={goToUpgrade}
            >
              Upgrade Now
            </Button>
          </Stack>
        </Alert>
      )}

      {/* ================= STAT CARDS ================= */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PendingActionsIcon />}
            label="Pending Subjects"
            value={summary.pending}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TimerIcon />}
            label="Ongoing Subjects"
            value={summary.in_progress}
          />
        </Grid>
      </Grid>

      {/* ================= LAST EXAM ================= */}
      {last_exam && (
        <Paper elevation={2} sx={{ p: 3, mt: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold">
            Last Exam Performance
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography fontWeight="bold">
            {last_exam.subject_name} — {last_exam.score}%
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Taken on{" "}
            {new Date(last_exam.submitted_at).toLocaleString()}
          </Typography>
        </Paper>
      )}

      {/* ================= BEST SUBJECT ================= */}
      {best_subject && (
        <Paper elevation={2} sx={{ p: 3, mt: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight="bold">
            Best Performing Subject
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <EmojiEventsIcon sx={{ color: "#FFD700" }} />
            <Typography fontWeight="bold">
              {best_subject.subject_name} — {best_subject.score}%
            </Typography>
          </Stack>
        </Paper>
      )}

      {/* ================= WEAK SUBJECTS ================= */}
      <Paper elevation={2} sx={{ p: 3, mt: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          Weak Subjects (Score &lt; 50)
        </Typography>
        <Divider sx={{ my: 2 }} />

        {weak_subjects.length ? (
          <>
            <List dense>
              {paginatedWeak.map((w, i) => (
                <ListItem key={i} divider>
                  <ListItemText
                    primary={w.subject_name}
                    secondary={`Score: ${w.score}%`}
                  />
                </ListItem>
              ))}
            </List>

            <TablePagination
              component="div"
              count={weak_subjects.length}
              page={weakPage}
              onPageChange={(e, p) => setWeakPage(p)}
              rowsPerPage={weakRows}
              onRowsPerPageChange={(e) => {
                setWeakRows(parseInt(e.target.value, 10));
                setWeakPage(0);
              }}
              rowsPerPageOptions={[5, 10]}
            />
          </>
        ) : (
          <Typography>No weak subjects. Excellent work!</Typography>
        )}
      </Paper>

      {/* ================= PERFORMANCE TREND ================= */}
      <Paper elevation={2} sx={{ p: 3, mt: 4, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          Performance Trend
        </Typography>
        <Divider sx={{ my: 2 }} />
        {trends.length ? (
          <Line data={chartData} height={80} />
        ) : (
          <Typography>No performance data yet.</Typography>
        )}
      </Paper>

      {/* ================= EXAM HISTORY ================= */}
      <Paper elevation={2} sx={{ p: 3, mt: 4, mb: 5, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight="bold">
          Exam History
        </Typography>
        <Divider sx={{ my: 2 }} />

        {all_results.length ? (
          <>
            <List dense>
              {paginatedHistory.map((r, i) => (
                <ListItem key={i} divider>
                  <ListItemText
                    primary={`${r.subject_name} — ${r.score}%`}
                    secondary={new Date(r.submitted_at).toLocaleString()}
                  />
                </ListItem>
              ))}
            </List>

            <TablePagination
              component="div"
              count={all_results.length}
              page={historyPage}
              onPageChange={(e, p) => setHistoryPage(p)}
              rowsPerPage={historyRows}
              onRowsPerPageChange={(e) => {
                setHistoryRows(parseInt(e.target.value, 10));
                setHistoryPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </>
        ) : (
          <Typography>No exam history yet.</Typography>
        )}
      </Paper>
    </Box>
  );
}
